import {
  AppBar,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import BadgeLink from "../../Components/BadgeLink";
import SortableTable from "../../Components/SortableTable";
import Title from "../../Components/Title";
import { getDevices } from "../../Utils/ApiCalls";
import {
  ECABIN,
  formatTimeWithTZOffset,
  getDeviceProfiles,
  getToken,
  requestErrorHandler,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import { useStyles } from "../../Utils/Styles";

const server = EnvSettings.server;

const normalizeSoftwareType = (value) =>
  (value || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

const isTruthyOrderSerialNoPreallocation = (value) => {
  if (value === undefined || value === null || value === "") {
    return false;
  }
  if (typeof value === "string") {
    return !["0", "false", "no"].includes(value.toLowerCase());
  }
  return value === true || Number(value) > 0;
};

const getSoftwareProfileType = (profile) =>
  profile?.ProductLine?.simplifiedName || normalizeSoftwareType(profile?.name);

const getSoftwareProfileKey = (profile) =>
  profile?.id || getSoftwareProfileType(profile);

const profileSupportsSoftwareReleases = (profile) =>
  isTruthyOrderSerialNoPreallocation(profile?.orderSerialNoPreallocation) &&
  !!getSoftwareProfileType(profile);

const getDeviceProfileFilter = (profile) =>
  profile?.id
    ? { DeviceProfileId: profile.id }
    : { search_value: profile?.prefix || getSoftwareProfileType(profile) };

export default function SoftwareDetails() {
  const classes = useStyles();
  const [deviceProfiles, setDeviceProfiles] = useState();
  const [selectedSoftwareTab, setSelectedSoftwareTab] = useState(0);
  const [softwarePackages, setSoftwarePackages] = useState();
  const [latestVersion, setLatestVersion] = useState();
  const [crashlogs, setCrashlogs] = useState();
  const [crashlogStats, setCrashlogStats] = useState();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const softwareReleaseProfiles = useMemo(() => {
    if (!Array.isArray(deviceProfiles)) {
      return [];
    }
    return deviceProfiles
      .filter(profileSupportsSoftwareReleases)
      .sort((a, b) => (a.name > b.name ? 1 : -1));
  }, [deviceProfiles]);
  const activeSoftwareProfile = softwareReleaseProfiles[selectedSoftwareTab];
  const activeSoftwareProfileKey = getSoftwareProfileKey(activeSoftwareProfile);
  const activeSoftwareProfileKeyRef = useRef(activeSoftwareProfileKey);

  useEffect(() => {
    activeSoftwareProfileKeyRef.current = activeSoftwareProfileKey;
  }, [activeSoftwareProfileKey]);

  const isActiveSoftwareProfile = (profile) =>
    activeSoftwareProfileKeyRef.current === getSoftwareProfileKey(profile);

  const formatSWData = (data) => {
    var arr = [];
    if (data && Array.isArray(data)) {
      data.forEach((d) => {
        var obj = {
          version: d.filename.replace(".zip", "").split("").join("."),
          date: formatTimeWithTZOffset(d.date, ECABIN).substring(0, 10),
          count: 0,
          percentage: 0,
        };
        arr.push(obj);
      });
    }
    return arr;
  };

  const formatCrashlogs = (data) => {
    var arr = [];
    if (data && Array.isArray(data)) {
      data.forEach((d) => {
        var obj = {
          id: d.id,
          date:
            d.type === 1
              ? d.date
              : formatTimeWithTZOffset(d.date, ECABIN).substring(0, 10),
          serialNumber: d.Device?.serialNumber,
          version: d.version,
          type: d.type,
          reason: d.reason,
        };
        arr.push(obj);
      });
    }
    return arr;
  };

  const getLatestSoftwarePackageVersions = (packages) => {
    if (!packages || !Array.isArray(packages)) {
      return [];
    }
    return [...packages]
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5)
      .map((p) => p.version)
      .filter((v) => !!v);
  };

  const getCrashlogStats = (versions, profile) => {
    if (!versions || versions.length === 0) {
      setCrashlogStats([]);
      return;
    }
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        count_only: true,
        count_versions: versions,
        ...(profile?.id ? { DeviceProfileId: profile.id } : {}),
      }),
    };
    fetch(server + "/get_crashlogs", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!isActiveSoftwareProfile(profile)) {
          return;
        }
        if (!result.error) {
          setCrashlogStats(result.crashlogCounts || []);
        }
      });
  };

  const getSoftwarePackages = (profile) => {
    const softwareType = getSoftwareProfileType(profile);
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: getToken(), type: softwareType }),
    };
    fetch(server + "/get_software_packages", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!isActiveSoftwareProfile(profile)) {
          return;
        }
        if (!result.error) {
          var packages = formatSWData(result.packages);
          getDevices(
            {
              view: "list",
              requireLCS: false,
              ...getDeviceProfileFilter(profile),
            },
            (result) => {
              if (!isActiveSoftwareProfile(profile)) {
                return;
              }
              if (!result.error && result.devices) {
                var arr = packages;
                result.devices.forEach((d) => {
                  var i = packages.findIndex((s) => s.version === d.version);
                  if (i === -1) {
                    return;
                  }
                  var s = arr[i];
                  s.count = s.count + 1;
                  s.percentage = s.count / result.devices.length;
                  arr[i] = s;
                });
                setSoftwarePackages(arr);
                getCrashlogStats(getLatestSoftwarePackageVersions(arr), profile);
              } else {
                setSoftwarePackages(packages);
                getCrashlogStats(
                  getLatestSoftwarePackageVersions(packages),
                  profile,
                );
              }
            },
          );
        } else {
          setSoftwarePackages([]);
          setCrashlogStats([]);
        }
      });
  };

  const getLatestCrashlogs = (profile) => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        limit: 5,
        ...(profile?.id ? { DeviceProfileId: profile.id } : {}),
      }),
    };
    fetch(server + "/get_crashlogs", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!isActiveSoftwareProfile(profile)) {
          return;
        }
        if (!result.error) {
          setCrashlogs(formatCrashlogs(result.crashlogs));
        }
      });
  };

  const getLatestVersion = (profile) => {
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        device_type: getSoftwareProfileType(profile),
      }),
    };
    fetch(server + "/get_latest_software_version", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!isActiveSoftwareProfile(profile)) {
          return;
        }
        if (!result.error) {
          setLatestVersion(result.data || result.message || "");
        }
      });
  };

  useEffect(() => {
    getDeviceProfiles(setDeviceProfiles, dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (
      softwareReleaseProfiles.length &&
      selectedSoftwareTab >= softwareReleaseProfiles.length
    ) {
      setSelectedSoftwareTab(0);
    }
  }, [selectedSoftwareTab, softwareReleaseProfiles.length]);

  useEffect(() => {
    if (!activeSoftwareProfile) {
      return;
    }
    setSoftwarePackages();
    setCrashlogs();
    setCrashlogStats();
    setLatestVersion();
    getSoftwarePackages(activeSoftwareProfile);
    getLatestCrashlogs(activeSoftwareProfile);
    getLatestVersion(activeSoftwareProfile);
    // eslint-disable-next-line
  }, [activeSoftwareProfileKey]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Grid item xs={12} md={1}>
          <BadgeLink
            href={"/cpanel/software/releases"}
            title={t("software.releases")}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          {deviceProfiles ? (
            softwareReleaseProfiles.length ? (
              <>
                <AppBar position="static" color="default">
                  <Tabs
                    value={selectedSoftwareTab}
                    onChange={(event, newValue) => {
                      setSelectedSoftwareTab(newValue);
                    }}
                    indicatorColor="primary"
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {softwareReleaseProfiles.map((profile) => (
                      <Tab
                        key={getSoftwareProfileKey(profile)}
                        label={profile.name}
                      />
                    ))}
                  </Tabs>
                </AppBar>
                {activeSoftwareProfile ? (
                  <Grid container spacing={3} style={{ marginTop: 16 }}>
                    <Grid item xs={12} md={4}>
                      <Title>
                        {t("software.releases")}: {activeSoftwareProfile.name}
                      </Title>
                      <Grid container>
                        {softwarePackages ? (
                          <SortableTable
                            headCells={[
                              {
                                id: "version",
                                numeric: false,
                                disablePadding: true,
                                label: t("version"),
                              },
                              {
                                id: "date",
                                numeric: false,
                                disablePadding: true,
                                label: t("releaseDate"),
                              },
                              {
                                id: "count",
                                numeric: false,
                                disablePadding: true,
                                label: "Active installs",
                              },
                              {
                                id: "percentage",
                                numeric: false,
                                disablePadding: true,
                                label: "%",
                              },
                            ]}
                            defaultSortRow={"date"}
                            handleRowClick={() => {}}
                            tableData={softwarePackages}
                            rowsPerPage={5}
                            disablePagination={true}
                            tableSize={"small"}
                            specialDataFormatting={[
                              {
                                id: "percentage",
                                format: (d) => (d * 100).toFixed(2),
                              },
                            ]}
                          />
                        ) : (
                          <CircularProgress />
                        )}
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Title>{t("controlPanel.totalCrashCount")}</Title>
                      <Grid container>
                        {crashlogStats ? (
                          <SortableTable
                            headCells={[
                              {
                                id: "version",
                                numeric: false,
                                disablePadding: true,
                                label: t("version"),
                              },
                              {
                                id: "count",
                                numeric: false,
                                disablePadding: true,
                                label: t("count"),
                              },
                            ]}
                            defaultSortRow={"version"}
                            handleRowClick={() => {}}
                            tableData={crashlogStats}
                            rowsPerPage={5}
                            disablePagination={true}
                            tableSize={"small"}
                          />
                        ) : (
                          <CircularProgress />
                        )}
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={5}>
                      <Title>{t("controlPanel.latestCrashReports")}</Title>
                      {crashlogs ? (
                        <SortableTable
                          headCells={[
                            {
                              id: "date",
                              numeric: false,
                              disablePadding: true,
                              label: t("date"),
                            },
                            {
                              id: "serialNumber",
                              numeric: false,
                              disablePadding: true,
                              label: t("serialNumber"),
                            },
                            {
                              id: "version",
                              numeric: false,
                              disablePadding: true,
                              label: t("version"),
                            },
                            {
                              id: "type",
                              numeric: false,
                              disablePadding: true,
                              label: t("type"),
                            },
                            {
                              id: "reason",
                              numeric: false,
                              disablePadding: true,
                              label: t("reason"),
                            },
                          ]}
                          defaultSortRow={"date"}
                          handleRowClick={(data) => {
                            history.push("/cpanel/crashlogs/" + data.id);
                          }}
                          handleScrollClick={(event, id) => {
                            if (window.location.href.includes("cpanel")) {
                              window.open("/cpanel/crashlogs/" + id.id, "_blank");
                            }
                          }}
                          tableData={crashlogs}
                          rowsPerPage={5}
                          disablePagination={true}
                          tableSize={"small"}
                        />
                      ) : (
                        <CircularProgress />
                      )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Title>Latest version: {activeSoftwareProfile.name}</Title>
                      <Typography style={{ whiteSpace: "pre-wrap" }}>
                        {latestVersion}
                      </Typography>
                    </Grid>
                  </Grid>
                ) : null}
              </>
            ) : (
              <Typography>
                No device profiles expecting software releases.
              </Typography>
            )
          ) : (
            <CircularProgress />
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
